import axios from "axios";

/** True when the error is an axios 409 conflict response. */
export const isConflictError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 409;

/** True for any axios 4xx response — the server rejected the request before acting on it. */
export const isClientRejectionError = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.response != null &&
  error.response.status >= 400 &&
  error.response.status < 500;

/** One entry of FastAPI's validation-error array as text, or null if unreadable. */
const validationLine = (entry: unknown, fieldNames: boolean): string | null => {
  if (typeof entry === "string") return entry || null;
  if (!entry || typeof entry !== "object") return null;
  const { loc, msg } = entry as { loc?: unknown; msg?: unknown };
  if (typeof msg !== "string" || !msg) return null;
  if (!fieldNames || !Array.isArray(loc)) return msg;
  // The "body"/"query" head names the request part, not the field.
  const field = loc
    .filter((part) => part !== "body" && part !== "query")
    .join(".");
  return field ? `${field}: ${msg}` : msg;
};

/** The response half of a rejected request, duck-typed so a re-thrown
 * rejection still reads; undefined when none came back. */
const responseOf = (
  err: unknown,
): { status?: unknown; data?: unknown } | undefined => {
  if (!err || typeof err !== "object") return undefined;
  const { response } = err as { response?: unknown };
  return response && typeof response === "object"
    ? (response as { status?: unknown; data?: unknown })
    : undefined;
};

/**
 * The server's own refusal as text, or null when it sent nothing readable.
 *
 * Duck-typed rather than gated on `axios.isAxiosError`, so a re-thrown
 * rejection still yields its detail, and never returns an object or array (a
 * pydantic `detail` rendered as a React child crashes the page). Handles a
 * string `detail`, a 422 array of `{loc, msg}`, and a structured
 * `{message, code}` the response interceptor did not already flatten.
 *
 * @param options.fieldNames Prefix each 422 message with the field it names.
 */
export const apiErrorDetail = (
  err: unknown,
  options: { fieldNames?: boolean } = {},
): string | null => {
  const data = responseOf(err)?.data;
  if (!data || typeof data !== "object" || !("detail" in data)) return null;
  const { detail } = data as { detail: unknown };
  if (typeof detail === "string") return detail || null;
  if (Array.isArray(detail)) {
    const lines = detail
      .map((entry) => validationLine(entry, options.fieldNames ?? false))
      .filter((line): line is string => Boolean(line));
    return lines.length ? lines.join("; ") : null;
  }
  if (detail && typeof detail === "object") {
    const { message } = detail as { message?: unknown };
    if (typeof message === "string" && message) return message;
  }
  return null;
};

/** The HTTP status of a rejected request; undefined when none came back. */
export const errorStatus = (err: unknown): number | undefined => {
  const status = responseOf(err)?.status;
  return typeof status === "number" ? status : undefined;
};

/**
 * The machine-readable code of a structured `{code, message}` detail.
 *
 * Read from `detail_code`, where the global response interceptor moves it when
 * it flattens the detail to its message, and from the raw object for a
 * rejection that never passed through the interceptor.
 */
export const errorCode = (err: unknown): string | undefined => {
  const data = responseOf(err)?.data;
  if (!data || typeof data !== "object") return undefined;
  const { detail_code: flattened, detail } = data as {
    detail_code?: unknown;
    detail?: unknown;
  };
  if (typeof flattened === "string") return flattened;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const { code } = detail as { code?: unknown };
    if (typeof code === "string") return code;
  }
  return undefined;
};

/**
 * True when the request may have reached the server without its answer
 * reaching us: no response at all (a dropped connection, a client timeout) or
 * a gateway that gave up waiting (502/503/504, Cloudflare's 524).
 */
export const outcomeUnknown = (err: unknown): boolean => {
  const status = errorStatus(err);
  return status === undefined || [502, 503, 504, 524].includes(status);
};

/**
 * Pull a user-displayable message out of an error returned by the API.
 *
 * The server's detail as `apiErrorDetail` reads it, else the error's own
 * message (a network failure), else `fallback`, so callers can always render
 * something.
 */
export const extractErrorDetail = (
  err: unknown,
  fallback = "Unknown error",
): string => {
  const detail = apiErrorDetail(err);
  if (detail) return detail;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
};
